import React, { useEffect, useState } from 'react';

const COOKIE_CONSENT_KEY = 'koncierz_cookie_consent';
const COOKIE_NAME = 'koncierz_cookie_consent';

const readConsentFromStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.status) return parsed;
    return null;
  } catch (error) {
    console.error('Failed reading cookie consent', error);
    return null;
  }
};

const writeConsent = (consent) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    const value = encodeURIComponent(JSON.stringify(consent));
    document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
  } catch (error) {
    console.error('Failed writing cookie consent', error);
  }
};

const cookieConsentInit = {
  status: 'pending',
  preferences: {
    functional: true,
    analytics: false,
    marketing: false,
  },
};

export default function CookieConsent() {
  const [consent, setConsent] = useState(() => {
    const saved = readConsentFromStorage();
    return saved || cookieConsentInit;
  });

  const [showCustomize, setShowCustomize] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(() => {
    const saved = readConsentFromStorage();
    return !saved || saved.status === 'pending';
  });

  useEffect(() => {
    const showEvent = () => setIsPanelOpen(true);
    window.addEventListener('koncierz:openCookieConsent', showEvent);
    return () => window.removeEventListener('koncierz:openCookieConsent', showEvent);
  }, []);

  useEffect(() => {
    if (consent.status !== 'pending') {
      writeConsent(consent);
    }
  }, [consent]);

  const acceptAll = () => {
    setConsent({ status: 'accepted', preferences: { functional: true, analytics: true, marketing: true } });
    setShowCustomize(false);
    setIsPanelOpen(false);
  };

  const rejectAll = () => {
    setConsent({ status: 'rejected', preferences: { functional: true, analytics: false, marketing: false } });
    setShowCustomize(false);
    setIsPanelOpen(false);
  };

  const saveCustom = () => {
    setConsent((current) => ({ ...current, status: 'accepted' }));
    setShowCustomize(false);
    setIsPanelOpen(false);
  };

  const setPref = (pref, value) => {
    setConsent((current) => ({
      ...current,
      preferences: {
        ...current.preferences,
        [pref]: value,
      },
    }));
  };

  const isVisible = isPanelOpen;

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-3 right-3 left-3 z-50 rounded-xl border border-slate-400 bg-slate-900/95 p-4 text-sm text-white shadow-lg md:left-auto md:right-6 md:w-96">
      <button onClick={() => setIsPanelOpen(false)} className="absolute top-2 right-2 rounded text-slate-300 hover:text-white">✕</button>
      <div className="mb-2 font-semibold">Cookie and Privacy Preferences</div>
      <div className="mb-3 text-slate-200">
        We use cookies and local storage to improve your experience, remember language preference, and enable app functionality.
        In Canada and the U.S., you may manage your preferences.
      </div>
      <div className="mb-3 text-xs text-slate-400">(Reminder: this banner stays until you choose Allow/Decline/Custom.)</div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={acceptAll} className="rounded bg-emerald-500 px-3 py-1.5 font-medium text-white hover:bg-emerald-600">Allow All</button>
        <button onClick={rejectAll} className="rounded bg-slate-700 px-3 py-1.5 font-medium text-white hover:bg-slate-600">Decline Non-essential</button>
        <button onClick={() => setShowCustomize((v) => !v)} className="rounded border border-slate-400 px-3 py-1.5 text-white hover:bg-slate-800">Custom</button>
      </div>

      {showCustomize && (
        <div className="mt-3 rounded border border-slate-700 bg-slate-800 p-3">
          <div className="mb-2 font-medium">Customize cookie preferences</div>
          <label className="flex items-center justify-between gap-3 text-slate-100">
            Functional cookies (required)
            <input type="checkbox" checked={true} disabled className="h-4 w-4" />
          </label>
          <label className="flex items-center justify-between gap-3 text-slate-100 mt-2">
            Analytics cookies (optional)
            <input type="checkbox" checked={consent.preferences.analytics} onChange={(e) => setPref('analytics', e.target.checked)} className="h-4 w-4" />
          </label>
          <label className="flex items-center justify-between gap-3 text-slate-100 mt-2">
            Marketing cookies (optional)
            <input type="checkbox" checked={consent.preferences.marketing} onChange={(e) => setPref('marketing', e.target.checked)} className="h-4 w-4" />
          </label>
          <button onClick={saveCustom} className="mt-3 rounded bg-blue-600 px-3 py-1.5 font-medium text-white hover:bg-blue-700">Save Preferences</button>
        </div>
      )}
    </div>
  );
}
